"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { statusLabel } from "@/lib/status-labels";
import { Badge, type BadgeVariant } from "@/components/ui/Badge";
import { TableRowSkeleton } from "@/components/ui/Skeleton";

interface Column {
  key: string;
  label: string;
  className?: string;
}

interface AdminTableProps {
  columns: Column[];
  children: React.ReactNode;
  loading?: boolean;
  empty?: React.ReactNode;
  /** Saat true, tampilkan pesan error + tombol "Coba Lagi" alih-alih empty state.
   *  Mencegah kegagalan fetch tersamar seolah "data kosong". */
  error?: boolean;
  /** Dipanggil saat admin menekan "Coba Lagi" (biasanya query.refetch). */
  onRetry?: () => void;
  errorMessage?: string;
}

export function AdminTable({
  columns,
  children,
  loading,
  empty,
  error,
  onRetry,
  errorMessage = "Gagal memuat data. Periksa koneksi internet.",
}: AdminTableProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  // Tabel dikunci `min-w-[720px]`; di layar 375px berarti ±345px kolom
  // tersembunyi — termasuk seluruh kolom AKSI — tanpa satu pun petunjuk bahwa
  // area ini bisa digeser. Diukur, bukan diasumsikan: gradien hanya muncul saat
  // memang masih ada yang bisa digulir ke arah itu.
  const [edges, setEdges] = useState({ left: false, right: false });
  const syncEdges = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setEdges({ left: el.scrollLeft > 1, right: max > 1 && el.scrollLeft < max - 1 });
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    syncEdges();
    const ro = new ResizeObserver(syncEdges);
    ro.observe(el);
    // Baris masuk/keluar mengubah scrollWidth tanpa memicu resize container.
    const mo = new MutationObserver(syncEdges);
    mo.observe(el, { childList: true, subtree: true });
    el.addEventListener("scroll", syncEdges, { passive: true });
    return () => {
      ro.disconnect();
      mo.disconnect();
      el.removeEventListener("scroll", syncEdges);
    };
  }, [syncEdges]);

  return (
    <div className="relative overflow-hidden rounded-[var(--radius-lg,12px)] border border-[rgba(0,0,0,0.07)] bg-white">
      {/* Gradien tepi — `pointer-events-none` supaya tidak menghalangi ketukan
          pada sel di bawahnya. Dekoratif, jadi aria-hidden. */}
      {edges.left && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-0 z-10 w-6 bg-[linear-gradient(to_right,rgba(0,0,0,0.10),transparent)]"
        />
      )}
      {edges.right && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 z-10 w-6 bg-[linear-gradient(to_left,rgba(0,0,0,0.10),transparent)]"
        />
      )}
      <div ref={scrollRef} className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-[rgba(0,0,0,0.07)] bg-[#F7F7F8]">
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className={cn(
                    "text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#6B7280]",
                    col.className
                  )}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <TableRowSkeleton cols={columns.length} rows={6} />
            ) : error ? (
              <tr>
                <td colSpan={columns.length}>
                  <div className="flex flex-col items-center justify-center gap-3 py-16">
                    <p className="text-sm text-red-500">{errorMessage}</p>
                    {onRetry && (
                      <button
                        onClick={onRetry}
                        className="text-xs font-medium text-primary hover:underline"
                      >
                        Coba Lagi
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : React.Children.count(children) === 0 ? (
              <tr>
                {/* Anchor coach-mark: langkah tur yang menyentuh baris punya
                    pasangan alternatif yang menganchor ke sini saat tabelnya
                    kosong — supaya tidak ada sorotan ke tempat kosong. */}
                <td colSpan={columns.length} data-tour="table-empty">{empty}</td>
              </tr>
            ) : (
              children
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function Tr({
  children,
  className,
  tourAnchor,
}: {
  children: React.ReactNode;
  className?: string;
  /** Anchor coach-mark — isi `index === 0` saja, lihat catatan di RowActionsMenu. */
  tourAnchor?: boolean;
}) {
  return (
    <tr
      data-tour={tourAnchor ? "table-first-row" : undefined}
      className={cn(
        "border-b border-[rgba(0,0,0,0.05)] last:border-0 even:bg-[#FAFAFA] hover:bg-[#F4F4F5] transition-colors duration-100",
        className
      )}
    >
      {children}
    </tr>
  );
}

export function Td({
  children,
  className,
  colSpan,
}: {
  children: React.ReactNode;
  className?: string;
  /** Dipakai baris detail yang membentang penuh, mis. panel perubahan di /admin/aktivitas. */
  colSpan?: number;
}) {
  return (
    <td colSpan={colSpan} className={cn("px-4 py-2.5 text-[13px] text-[#3F3F46]", className)}>{children}</td>
  );
}

/**
 * Peta status → varian Badge.
 *
 * Dulu komponen ini menyalin sendiri bentuk dan warnanya — string dasarnya
 * identik byte-per-byte dengan `Badge`, tapi `Badge` nol importer sementara ini
 * dipakai 38×. Dua komponen untuk satu hal, yang satu hidup satu mati.
 * Sekarang bentuk & warna hanya ada di `Badge`; di sini tinggal pemetaannya.
 */
const STATUS_VARIANT: Record<string, BadgeVariant> = {
  paid: "success",
  published: "success",
  active: "success",
  approved: "success",
  free: "success",
  pending: "warning",
  draft: "warning",
  scheduled: "info",
  sending: "info",
  sent: "success",
  paused: "gray",
  processed: "success",
  failed: "error",
  cancelled: "error",
  rejected: "error",
  archived: "gray",
  inactive: "gray",
  closed: "gray",
  expired: "gray",
  past_due: "warning",
  admin_locked: "warning",
  checkout_disabled: "error",
  suspended: "error",
  user: "gray",
  admin: "primary",
  super_admin: "primary",
  membership_only: "purple",
  // Pendaftaran webinar
  registered: "success",
  registered_free: "success",
  registered_paid: "success",
  share_confirmation_pending: "warning",
  payment_pending: "warning",
};

export function StatusBadge({ status }: { status: string }) {
  return <Badge variant={STATUS_VARIANT[status] ?? "gray"}>{statusLabel(status)}</Badge>;
}
